import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Shield, Star, Users, Heart, AlertTriangle, Coins, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl() + '/api';

const GuideCharterPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  // Don't pre-fill with 'pending' - user must explicitly choose
  const [guideType, setGuideType] = useState(
    user?.guide_type && user.guide_type !== 'pending' ? user.guide_type : ''
  );
  const [creatorType, setCreatorType] = useState(user?.creator_type || '');
  const [loading, setLoading] = useState(false);

  const handleAcceptCharter = async () => {
    // Check authentication first
    if (!isAuthenticated) {
      toast.error('Veuillez vous connecter pour accepter la charte');
      navigate('/login?redirect=/guide-charter');
      return;
    }

    // Check if user is a guide
    if (user?.role !== 'explorer') {
      toast.error('Seuls les guides peuvent accepter cette charte');
      return;
    }

    if (!accepted || !termsAccepted) {
      toast.error('Veuillez accepter la charte et les conditions d\'utilisation');
      return;
    }

    if (!guideType) {
      toast.error('Veuillez choisir votre mode d\'activité');
      return;
    }

    if (!creatorType) {
      toast.error('Veuillez choisir votre type d\'expérience');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vgt_token');
      if (!token) {
        toast.error('Session expirée. Veuillez vous reconnecter.');
        navigate('/login?redirect=/guide-charter');
        return;
      }

      await axios.post(`${API_URL}/explorer/accept-charter`, {
        accepted: true,
        charter_version: "1.0",
        guide_type: guideType,
        creator_type: creatorType
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      toast.success('Charte acceptée ! Bienvenue dans la communauté XPerience !');
      
      if (refreshUser) {
        await refreshUser();
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Charter acceptance error:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'acceptation de la charte';
      toast.error(errorMsg);
      
      // If unauthorized, redirect to login
      if (error.response?.status === 401) {
        navigate('/login?redirect=/guide-charter');
      }
    } finally {
      setLoading(false);
    }
  };

  const rules = [
    { icon: CheckCircle, text: 'Je suis sérieux', color: 'text-green-600' },
    { icon: Shield, text: 'Je donne des informations vraies', color: 'text-blue-600' },
    { icon: Users, text: 'Je suis disponible et courtois', color: 'text-purple-600' },
    { icon: Heart, text: 'Je m\'engage à honorer mes prestations avec bienveillance', color: 'text-red-500' }
  ];

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[30vh] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920&q=80"
          alt="Guide adventure"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-bold font-serif mb-4">Charte des Guides</h1>
            <p className="text-xl text-white/80">Bienvenue dans la communauté XPerience</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            {/* Greeting */}
            <div className="text-center mb-8">
              <span className="text-5xl mb-4 block">👋</span>
              <h2 className="text-2xl font-bold text-gray-800">
                Bienvenue dans la communauté XPerience !
              </h2>
              <p className="text-gray-600 mt-2">
                Guides, artisans, praticiens bien-être, organisateurs d'événements...
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p>
                Voici un bref résumé de ce que nous attendons des personnes qui s'inscrivent pour proposer des <strong>expériences authentiques</strong>.
              </p>

              {/* FREE FOR GUIDES - NEW PROMINENT SECTION */}
              <div className="bg-green-50 border-2 border-green-300 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-200 rounded-full flex items-center justify-center">
                    <span className="text-2xl">🎁</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-green-800 mb-0">100% GRATUIT pour les guides</h3>
                    <p className="text-green-600 text-sm mb-0">Aucun abonnement, aucun frais d'inscription</p>
                  </div>
                </div>
                <div className="space-y-2 text-green-800">
                  <p className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span><strong>Inscription gratuite</strong> - Créez votre profil guide sans frais</span>
                  </p>
                  <p className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span><strong>Publication gratuite</strong> - Créez autant d'expériences que vous voulez</span>
                  </p>
                  <p className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span><strong>Expériences gratuites</strong> - Proposez des rencontres sans aucun frais</span>
                  </p>
                  <p className="flex items-center gap-2 mb-0">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span><strong>Commission uniquement sur les ventes</strong> - 15% prélevé seulement quand vous êtes payé</span>
                  </p>
                </div>
              </div>

              <div className="bg-orange-50 border-l-4 border-primary p-6 rounded-r-lg">
                <p className="mb-0">
                  Tu crées un profil avec photo si possible. Tu établis une liste de choses que tu sais faire découvrir, que ce soit touristique ou pas, et peut-être aussi des aspects moins connus du grand public.
                </p>
                <p className="mt-4 mb-0 font-medium text-primary">
                  Bref, tu gères, et je suis sûr que tu sauras surprendre tes hôtes !
                </p>
              </div>

              <p>
                Tu établis un <strong>prix correct</strong> de prestations et tu t'engages dans ta mission sérieusement, car <strong>tu es ton propre patron</strong>.
              </p>

              {/* Business Models Section */}
              <div className="bg-gradient-to-r from-primary/10 to-green-50 p-6 rounded-xl border border-primary/20">
                <h3 className="text-lg font-bold text-gray-800 mb-4">📋 Trois façons de partager sur XPerience :</h3>
                
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">💰</span>
                      <strong className="text-primary">Expériences payantes</strong>
                    </div>
                    <p className="text-sm text-gray-600 mb-0">
                      Tu fixes ton prix librement, les voyageurs paient lors de la réservation. 
                      XPerience prélève <strong>15% de commission</strong> pour couvrir les frais de paiement, 
                      l'assurance et le fonctionnement de la plateforme.
                    </p>
                    <p className="text-sm text-green-600 font-medium mt-2 mb-0">
                      ✓ Ouvert à tous les voyageurs, membre ou non
                    </p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🤝</span>
                      <strong className="text-green-600">Rencontres gratuites (Meetups)</strong>
                    </div>
                    <p className="text-sm text-gray-600 mb-0">
                      Tu partages par passion, sans rémunération. Ces expériences sont réservées 
                      aux <strong>voyageurs membres</strong> (abonnement 49€/an). C'est l'esprit communautaire !
                    </p>
                    <p className="text-sm text-purple-600 font-medium mt-2 mb-0">
                      ✓ Création gratuite pour toi, le guide
                    </p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">🎪</span>
                      <strong className="text-purple-600">Événements locaux</strong>
                    </div>
                    <p className="text-sm text-gray-600 mb-0">
                      Tu organises un festival, une fête locale ou un marché ? 
                      <strong> Publication 100% gratuite et ouverte à tous !</strong> XPerience soutient la vie locale.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment & Escrow System */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-blue-800 mb-3">💳 Système de paiement sécurisé</h3>
                <ul className="text-sm text-blue-900 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Les paiements sont <strong>retenus par XPerience</strong> jusqu'à confirmation de l'expérience par le voyageur.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500">•</span>
                    <span>Tu reçois ton paiement <strong>48h après</strong> que le voyageur ait confirmé la réalisation de l'expérience.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500">•</span>
                    <span>En cas de litige, XPerience examine la situation et prend une décision équitable.</span>
                  </li>
                </ul>
              </div>

              {/* Cancellation Policy */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-amber-800 mb-3">📅 Politique d'annulation uniforme</h3>
                <ul className="text-sm text-amber-900 space-y-2">
                  <li><strong>Plus de 7 jours avant :</strong> Remboursement intégral</li>
                  <li><strong>3 à 7 jours avant :</strong> Remboursement de 50%</li>
                  <li><strong>Moins de 3 jours :</strong> Pas de remboursement</li>
                  <li><strong>Annulation par le guide :</strong> Remboursement intégral au voyageur</li>
                </ul>
              </div>

              <p>
                Dès que la prestation est complète et validée, tu reçois le paiement et tes clients peuvent te noter. Dès le moment où des explorateurs prennent contact avec toi, tu as une opportunité de faire vivre une <strong className="text-primary">Xpérience</strong> !
              </p>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <p className="mb-0 text-amber-800">
                  La plateforme peut à tout moment bloquer ou supprimer des utilisateurs qui ne respectent pas les règles de la communauté.
                </p>
              </div>

              {/* Rules */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-6">En voici quelques-unes :</h3>
                <div className="grid gap-4">
                  {rules.map((rule, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                      className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg"
                    >
                      <rule.icon className={`w-6 h-6 ${rule.color} flex-shrink-0`} />
                      <span className="text-gray-700 font-medium">{rule.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Acceptance Section */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              {/* Creator Type Selection - NEW */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Quel type d'expérience proposez-vous ?
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choisissez la catégorie qui correspond le mieux à votre activité.
                </p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { id: 'guide', icon: '🧭', label: 'Guide local', desc: 'Visites, randonnées, tours' },
                    { id: 'artisan', icon: '🎨', label: 'Artisan', desc: 'Ateliers créatifs' },
                    { id: 'facilitator', icon: '🧘', label: 'Facilitateur', desc: 'Retraites, bien-être, yoga' },
                    { id: 'organizer', icon: '🎪', label: 'Organisateur', desc: 'Événements, festivals' }
                  ].map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setCreatorType(type.id)}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        creatorType === type.id 
                          ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/30' 
                          : 'border-gray-200 hover:border-primary/50 bg-white'
                      }`}
                      data-testid={`creator-type-${type.id}`}
                    >
                      <span className="text-3xl block mb-2">{type.icon}</span>
                      <span className="font-bold text-sm block">{type.label}</span>
                      <span className="text-xs text-gray-500">{type.desc}</span>
                      {creatorType === type.id && (
                        <CheckCircle className="w-5 h-5 text-primary mx-auto mt-2" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Guide Type Selection - Mandatory */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  Mon mode d'activité
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choisissez comment vous souhaitez partager vos expériences. Vous pourrez modifier ce choix plus tard dans votre profil.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Paid Guide */}
                  <button
                    type="button"
                    onClick={() => setGuideType('retribution')}
                    className={`p-5 rounded-xl border-2 transition-all text-left ${
                      guideType === 'retribution' 
                        ? 'border-orange-500 bg-orange-50 shadow-md ring-2 ring-orange-200' 
                        : 'border-gray-200 hover:border-orange-300 bg-white'
                    }`}
                    data-testid="charter-guide-type-retribution"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        guideType === 'retribution' ? 'bg-orange-200' : 'bg-gray-100'
                      }`}>
                        <Coins className={`w-6 h-6 ${guideType === 'retribution' ? 'text-orange-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <span className="font-bold text-lg block">Je partage contre rétribution</span>
                        <span className="text-sm text-orange-600">Expériences payantes</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Je fixe mes prix librement. Commission de 15% pour la plateforme.
                      Paiements sécurisés via Stripe.
                    </p>
                    {guideType === 'retribution' && (
                      <div className="mt-3 flex items-center gap-2 text-orange-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Sélectionné</span>
                      </div>
                    )}
                  </button>

                  {/* Passion Guide */}
                  <button
                    type="button"
                    onClick={() => setGuideType('passion')}
                    className={`p-5 rounded-xl border-2 transition-all text-left ${
                      guideType === 'passion' 
                        ? 'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200' 
                        : 'border-gray-200 hover:border-green-300 bg-white'
                    }`}
                    data-testid="charter-guide-type-passion"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        guideType === 'passion' ? 'bg-green-200' : 'bg-gray-100'
                      }`}>
                        <Heart className={`w-6 h-6 ${guideType === 'passion' ? 'text-green-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <span className="font-bold text-lg block">Je partage par passion</span>
                        <span className="text-sm text-green-600">Expériences gratuites</span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      Je partage bénévolement. Les participants peuvent faire une donation
                      volontaire s'ils le souhaitent.
                    </p>
                    {guideType === 'passion' && (
                      <div className="mt-3 flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Sélectionné</span>
                      </div>
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {/* Charter Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="charter"
                    checked={accepted}
                    onCheckedChange={setAccepted}
                    className="mt-1"
                    data-testid="charter-checkbox"
                  />
                  <label htmlFor="charter" className="text-gray-700 cursor-pointer">
                    <strong>J'accepte la charte des guides XPerience</strong> et m'engage à respecter ces valeurs dans toutes mes interactions.
                  </label>
                </div>

                {/* Terms Checkbox */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={setTermsAccepted}
                    className="mt-1"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms" className="text-gray-700 cursor-pointer">
                    J'accepte les <a href="/terms" className="text-primary hover:underline">conditions d'utilisation</a> et la <a href="/privacy" className="text-primary hover:underline">politique de confidentialité</a> du site.
                  </label>
                </div>
              </div>

              {/* Accept Button */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-8"
              >
                <Button
                  onClick={handleAcceptCharter}
                  disabled={!accepted || !termsAccepted || !guideType || !creatorType || loading}
                  className="w-full py-6 text-lg font-semibold"
                  data-testid="accept-charter-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  {loading ? 'Enregistrement...' : 'J\'accepte la charte et je rejoins XPerience'}
                </Button>
                {(!creatorType || !guideType) && (
                  <p className="text-center text-amber-600 mt-2 text-sm">
                    {!creatorType && !guideType 
                      ? 'Veuillez choisir votre type d\'expérience et votre mode d\'activité'
                      : !creatorType 
                        ? 'Veuillez choisir votre type d\'expérience ci-dessus'
                        : 'Veuillez choisir votre mode d\'activité ci-dessus'
                    }
                  </p>
                )}
              </motion.div>

              {/* Already registered */}
              {!isAuthenticated && (
                <p className="text-center text-gray-500 mt-4 text-sm">
                  Déjà inscrit ? <a href="/login" className="text-primary hover:underline">Connectez-vous</a>
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default GuideCharterPage;
